import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, Menu, X } from "lucide-react";
import { LogoMark } from "@/components/LogoMark";
import { useLanguage } from "@/context/LanguageContext";
import { SOLUTIONS } from "@/data/solutions";

export const Header = () => {
  const { t, lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  const navLinkClass = ({ isActive }) =>
    `text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
      isActive ? "text-bronze" : "text-ink hover:text-bronze"
    }`;

  const LangSwitch = ({ testPrefix }) => (
    <div className="flex items-center gap-1 text-sm font-medium" data-testid={`${testPrefix}-lang-switcher`}>
      {["ro", "en"].map((l) => (
        <button
          key={l}
          data-testid={`${testPrefix}-lang-${l}-button`}
          onClick={() => setLang(l)}
          aria-label={`Switch language to ${l.toUpperCase()}`}
          className={`px-2 py-1 uppercase tracking-wider transition-colors duration-300 ${
            lang === l ? "text-bronze" : "text-stone hover:text-ink"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );

  return (
    <header className="fixed top-0 z-50 w-full border-b border-line bg-white/95 backdrop-blur-xl">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-10">
        <Link data-testid="header-logo-link" to="/" className="flex items-center gap-3" onClick={() => setOpen(false)}>
          <LogoMark className="h-11 w-9 shrink-0" />
          <span className="leading-none">
            <span className="block text-[10px] uppercase tracking-[0.3em] text-stone">Scutul</span>
            <span className="block font-serif text-xl text-ink">Fermierului</span>
          </span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          <NavLink data-testid="nav-home-link" to="/" end className={navLinkClass}>{t.nav.home}</NavLink>
          <NavLink data-testid="nav-about-link" to="/despre" className={navLinkClass}>{t.nav.about}</NavLink>
          <NavLink data-testid="nav-method-link" to="/metoda" className={navLinkClass}>{t.nav.method}</NavLink>
          <div className="group relative">
            <button
              data-testid="nav-solutions-dropdown"
              className={`flex items-center gap-1 text-xs uppercase tracking-[0.18em] transition-colors duration-300 ${
                pathname.startsWith("/solutii") ? "text-bronze" : "text-ink hover:text-bronze"
              }`}
            >
              {t.nav.solutions}
              <ChevronDown className="h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180" />
            </button>
            <div className="invisible absolute left-1/2 top-full -translate-x-1/2 pt-5 opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
              <div className="min-w-[300px] border border-line bg-white py-3 shadow-2xl shadow-ink/10">
                {SOLUTIONS.map((s) => (
                  <Link
                    key={s.slug}
                    data-testid={`nav-solution-${s.slug}`}
                    to={`/solutii/${s.slug}`}
                    className="block px-5 py-3 text-sm text-ink/80 transition-colors duration-200 hover:bg-sand hover:text-bronze"
                  >
                    {s[lang].name}
                  </Link>
                ))}
                <Link
                  data-testid="nav-solution-all"
                  to="/solutii"
                  className="block border-t border-line px-5 py-3 text-xs uppercase tracking-widest text-bronze transition-colors duration-200 hover:bg-sand"
                >
                  {t.solutionsSection.all}
                </Link>
              </div>
            </div>
          </div>
          <NavLink data-testid="nav-blog-link" to="/blog" className={navLinkClass}>{t.nav.blog}</NavLink>
          <NavLink data-testid="nav-contact-link" to="/contact" className={navLinkClass}>{t.nav.contact}</NavLink>
        </nav>

        <div className="hidden items-center gap-6 lg:flex">
          <LangSwitch testPrefix="desktop" />
          <Link
            data-testid="nav-cta-button"
            to="/contact"
            className="border border-ink px-6 py-2.5 text-xs font-medium uppercase tracking-widest text-ink transition-colors duration-300 hover:bg-ink hover:text-ivory"
          >
            {t.nav.cta}
          </Link>
        </div>

        <button
          data-testid="mobile-menu-button"
          className="text-ink lg:hidden"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-line bg-white lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {[
                { to: "/", label: t.nav.home, id: "mobile-nav-home" },
                { to: "/despre", label: t.nav.about, id: "mobile-nav-about" },
                { to: "/metoda", label: t.nav.method, id: "mobile-nav-method" },
                { to: "/solutii", label: t.nav.solutions, id: "mobile-nav-solutions" },
                { to: "/blog", label: t.nav.blog, id: "mobile-nav-blog" },
                { to: "/contact", label: t.nav.contact, id: "mobile-nav-contact" },
              ].map((l) => (
                <Link
                  key={l.to}
                  data-testid={l.id}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  className="py-3 font-serif text-2xl text-ink transition-colors duration-200 hover:text-bronze"
                >
                  {l.label}
                </Link>
              ))}
              <div className="mt-2 border-t border-line pt-4">
                {SOLUTIONS.map((s) => (
                  <Link
                    key={s.slug}
                    data-testid={`mobile-nav-solution-${s.slug}`}
                    to={`/solutii/${s.slug}`}
                    onClick={() => setOpen(false)}
                    className="block py-2 text-sm text-stone transition-colors duration-200 hover:text-bronze"
                  >
                    {s[lang].name}
                  </Link>
                ))}
              </div>
              <div className="mt-4 flex items-center justify-between border-t border-line pt-4">
                <LangSwitch testPrefix="mobile" />
                <Link
                  data-testid="mobile-nav-cta-button"
                  to="/contact"
                  onClick={() => setOpen(false)}
                  className="bg-bronze px-5 py-2.5 text-xs font-medium uppercase tracking-widest text-ink"
                >
                  {t.nav.cta}
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
